import { DateTime } from '@ksyos/date-time';
import { Box, FormGroup, FormGroupContext, FormParagraph, Heading, Text } from '@ksyos/design-system';
import { capitalize } from 'lodash';
import { formatString } from '@common/utils/formatString';
import { getSelectedLanguage } from '@frontend/translate';

interface IValue {
    createdAt: DateTime;
    practitionerName: string;
    practitionerProfession: string;
    properties: { key: string; label: string; value: string }[];
}

interface IAddendumReadProps<TValue extends IValue> {
    value: TValue;
}

export function AddendumRead<TValue extends IValue>(props: IAddendumReadProps<TValue>) {
    const translate = getSelectedLanguage();
    const { value } = props;

    const createdAt = (() => {
        const format = 'DD-MM-YYYY';
        const now = DateTime.now();
        const date = DateTime.from(value.createdAt);
        const dateString = date.format(format);

        if (dateString === now.format(format)) {
            return capitalize(translate.calendarComponent.today);
        }

        if (dateString === now.subtract(1, 'day').format(format)) {
            return capitalize(translate.calendarComponent.yesterday);
        }

        return date.format(format);
    })();

    return (
        <FormGroupContext.Provider
            value={{ labelWidth: 'one-third', layout: 'horizontal', size: 'large', variant: 'outline' }}
        >
            <Box
                backgroundColor={'yellow100'}
                display={'flex'}
                flexDirection={'column'}
                gap={4}
                paddingBottom={8}
                paddingTop={4}
                paddingX={8}
            >
                <Heading size="medium">{translate.addendum.addendum}</Heading>
                <Text>
                    {formatString(translate.addendum.authorAndTime, [
                        createdAt,
                        value.createdAt.format('HH:mm'),
                        value.practitionerName,
                        value.practitionerProfession,
                    ])}
                </Text>
                <Box>
                    {value.properties
                        .filter((property) => property.value !== undefined)
                        .map(({ key: entryKey, value: entryValue, label: entryLabel }) => (
                            <FormGroup
                                key={entryKey}
                                label={formatString(translate.addendum.edited, [entryLabel])}
                            >
                                <FormParagraph>{entryValue}</FormParagraph>
                            </FormGroup>
                        ))}
                </Box>
            </Box>
        </FormGroupContext.Provider>
    );
}
